<template>
  <ScrollableTable ref="scroll" class="hk-board-table" @scroll="onScroll" v-loading="loading && !rows.length" element-loading-background="var(--theme-bg)">
    <table>
      <thead><tr><th class="sequence">序号</th><th v-for="column in columns" :key="column.key">{{ column.title }}</th></tr></thead>
      <tbody>
        <tr v-for="(item, index) in sharedStockRows(rows, 'hk', 'hk-board')" :key="item.code" tabindex="0" @click="$emit('select-stock', item)" @keydown.enter="$emit('select-stock', item)">
          <td>{{ index + 1 }}</td>
          <td v-for="column in columns" :key="column.key" :class="valueClass(item, column)" :title="format(item, column)">{{ format(item, column) }}</td>
        </tr>
      </tbody>
    </table>
    <div v-if="error" class="table-state">{{ error }} <button type="button" @click="load">重试</button></div>
    <div v-else-if="loading" class="table-state">加载中…</div>
    <div v-else-if="!rows.length" class="table-state">暂无板块行情</div>
    <div v-else-if="rows.length >= total" class="table-state">已加载全部 {{ total }} 只股票</div>
    <div v-else class="table-state"><button type="button" @click="load">加载更多（已加载 {{ rows.length }} / {{ total }}）</button></div>
  </ScrollableTable>
</template>

<script>
import { quoteDisplay, quotePrice } from '@/utils/quoteDisplay'
import { sharedStockRows } from '@/utils/stockQuoteFeed'
import { getHongKongBoard } from '../api'
import { pollMarket } from '@/utils/marketRefresh'
import { getStockQuotes } from '@/utils/stockQuotes'
import ScrollableTable from '@/components/ScrollableTable.vue'

export default {
  name: 'HongKongBoardTable',
  components: { ScrollableTable },
  props: { board: { type: String, required: true }, active: { type: Boolean, default: true } },
  data() {
    return {
      rows: [], total: 0, page: 1, loading: false, error: '', requestId: 0,
      columns: [
        { key: 'code', title: '代码' }, { key: 'name', title: '名称' },
        { key: 'zxj', title: '最新价' }, { key: 'zd', title: '涨跌额', change: true },
        { key: 'zdf', title: '涨跌幅', percent: true, change: true },
        { key: 'turnover', title: '成交额（万港元）', divisor: 10000, digits: 2 }, { key: 'volume', title: '成交量（万）', divisor: 10000, digits: 2 },
        { key: 'zf', title: '振幅(%)', percent: true },
        { key: 'hsl', title: '换手率', percent: true }, { key: 'pe_ttm', title: '市盈率(TTM)' },
        { key: 'pn', title: '市净率' }, { key: 'lb', title: '量比' },
        { key: 'zsz', title: '总市值（亿港元）', digits: 2 }, { key: 'ltsz', title: '流通市值（亿港元）', digits: 2 }
      ]
    }
  },
  watch: { board() { this.reset() }, active(value) { if (value) this.activate(); else this.stopPolling() } },
  mounted() { if (this.active) this.activate() },
  beforeDestroy() { this.disposed = true; this.requestId++; this.stopPolling() },
  methods: {
    sharedStockRows,
    stopPolling() { if (this.stopRefresh) this.stopRefresh(); this.stopRefresh = null },
    async activate() {
      if (this.disposed || !this.active || this.stopRefresh) return
      try { if (this.rows.length) await this.refreshPages(); else await this.load() }
      catch (_) { /* Preserve the cached table on quote failures. */ }
      finally {
        if (!this.disposed && this.active && !this.stopRefresh) this.stopRefresh = pollMarket(() => this.refreshPages(), 'hk')
      }
    },
    async refreshPages() {
      if (this.disposed || !this.active || this.loading || this.page < 2) return
      const requestId = this.requestId; this.loading = true
      try {
        if (!this.rows.length) return
        const codeOf = item => /^hk/i.test(item.code) ? item.code : 'hk' + item.code
        const quotes = await getStockQuotes(this.rows.map(codeOf).join(','))
        if (requestId !== this.requestId) return
        this.rows = this.rows.map(item => {
          const quote = quotes[codeOf(item)]
          return Array.isArray(quote) && Number(quote[3]) > 0 ? { ...item, zxj: quote[3], zd: quote[31], zdf: quote[32], turnover: quote[37], volume: quote[36], zf: quote[43], hsl: quote[59], pe_ttm: quote[57], pn: quote[58], zsz: quote[45], ltsz: quote[44] } : item
        })
      } finally { if (requestId === this.requestId) this.loading = false }
    },
    reset() {
      this.requestId++
      this.rows = []
      this.total = 0
      this.page = 1
      this.loading = false
      this.error = ''
      if (this.$refs.scroll) this.$refs.scroll.reset()
      this.load()
    },
    async load() {
      if (this.disposed || !this.active || this.loading) return
      const requestId = this.requestId
      this.loading = true
      this.error = ''
      try {
        const result = await getHongKongBoard({ board: this.board, page: this.page, page_size: 20 })
        if (requestId !== this.requestId) return
        if (!result || !Array.isArray(result.data)) throw new Error('invalid response')
        const known = new Set(this.rows.map(item => item.code))
        this.rows = this.rows.concat(result.data.filter(item => !known.has(item.code)))
        this.total = Number(result.total || 0)
        this.page++
      } catch (error) {
        if (requestId === this.requestId) this.error = '板块行情加载失败，请重试。'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    onScroll(event) {
      const element = event.target
      if (!this.active || this.disposed || element.clientHeight <= 0) return
      if (!this.error && this.rows.length < this.total && element.scrollTop + element.clientHeight >= element.scrollHeight - 40) this.load()
    },
    format(item, column) {
      const value = item[column.key]
      if (value == null || value === '') return '--'
      if (column.key === 'code') return String(value).replace(/^hk/i, '')
      if (column.key === 'zxj' || column.key === 'zd') return quotePrice(value)
      if (column.divisor || column.digits != null) return quoteDisplay(value, '', column.divisor || 1, column.digits)
      return column.percent || column.change ? quoteDisplay(value, column.percent ? '%' : '') : value
    },
    valueClass(item, column) {
      if (!column.change) return ''
      return Number(item[column.key]) > 0 ? 'rise' : Number(item[column.key]) < 0 ? 'fall' : ''
    }
  }
}
</script>

<style scoped>
.hk-board-table { flex: 1; min-height: 0; overflow: hidden; background: var(--theme-bg); color: var(--theme-label); font-size: 14px; }
table { width: 100%; min-width: 1800px; table-layout: fixed; border-collapse: collapse; text-align: center; }
th, td { height: 31px; padding: 0 6px; font-weight: normal; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
th { position: sticky; top: 0; z-index: 1; background: var(--theme-header); }
.sequence { width: 50px; }
tbody tr { cursor: pointer; } tbody tr:hover { background: var(--theme-panel); }
.rise { color: var(--quote-up); } .fall { color: var(--quote-down); }
.table-state { padding: 12px; text-align: center; }
button { background: transparent; color: var(--theme-label); border: 1px solid var(--theme-label); cursor: pointer; }
</style>
